import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Linking } from 'react-native';
import { Text, Card, Button, Divider, ActivityIndicator, List, Chip } from 'react-native-paper';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const ProveedorDetalleScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { proveedorId } = route.params || {};
  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProveedor = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getProveedor(proveedorId);
      setProveedor(data);
    } catch (error) {
      console.error('Error al cargar proveedor:', error);
      Alert.alert('Error', 'No se pudo cargar la información del proveedor');
    } finally {
      setLoading(false);
    }
  }, [proveedorId]);

  useFocusEffect(
    useCallback(() => {
      loadProveedor();
    }, [loadProveedor])
  );

  const handleToggleEstado = async () => {
    const nuevoEstado = proveedor.estado === 'activo' ? 'inactivo' : 'activo';
    const accion = nuevoEstado === 'inactivo' ? 'desactivar' : 'activar';

    Alert.alert(
      `Confirmar ${accion}`,
      `¿Está seguro que desea ${accion} este proveedor?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              if (nuevoEstado === 'inactivo') {
                await api.deleteProveedor(proveedorId);
              } else {
                await api.updateProveedor(proveedorId, { estado: 'activo' });
              }
              setProveedor({ ...proveedor, estado: nuevoEstado });
              Alert.alert('Éxito', `Proveedor ${nuevoEstado === 'activo' ? 'activado' : 'desactivado'}`);
            } catch (error) {
              Alert.alert('Error', error.message || 'No se pudo cambiar el estado');
            }
          }
        }
      ]
    );
  };

  const handleCall = () => {
    if (proveedor.telefono) Linking.openURL(`tel:${proveedor.telefono}`);
  };

  const handleEmail = () => {
    if (proveedor.email) Linking.openURL(`mailto:${proveedor.email}`);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-HN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando información del proveedor...</Text>
      </View>
    );
  }

  if (!proveedor) {
    return (
      <View style={styles.loaderContainer}>
        <Ionicons name="alert-circle-outline" size={64} color="#ccc" />
        <Text style={styles.loaderText}>Proveedor no encontrado</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.proveedorName}>{proveedor.nombre}</Text>
              {proveedor.rtn && <Text style={styles.rtnText}>RTN: {proveedor.rtn}</Text>}
            </View>
            <Chip
              mode="outlined"
              style={[styles.estadoChip, proveedor.estado === 'activo' ? styles.chipActivo : styles.chipInactivo]}
            >
              {proveedor.estado === 'activo' ? 'Activo' : 'Inactivo'}
            </Chip>
          </View>

          <View style={styles.actionButtons}>
            <Button
              mode="contained"
              icon="cart"
              onPress={() => navigation.navigate('NuevaCompra', { proveedorId: proveedor.id })}
              style={styles.pedidoButton}
              compact
            >
              Nueva OC
            </Button>
            <Button
              mode="outlined"
              icon={proveedor.estado === 'activo' ? "close-circle" : "check-circle"}
              onPress={handleToggleEstado}
              compact
            >
              {proveedor.estado === 'activo' ? 'Desactivar' : 'Activar'}
            </Button>
          </View>

          <Divider style={styles.divider} />

          <Text style={styles.sectionTitle}>Contacto</Text>

          <View style={styles.contactButtons}>
            {proveedor.telefono && (
              <Button mode="contained-tonal" icon="phone" onPress={handleCall} style={styles.contactButton} compact>
                Llamar
              </Button>
            )}
            {proveedor.email && (
              <Button mode="contained-tonal" icon="email" onPress={handleEmail} style={styles.contactButton} compact>
                Email
              </Button>
            )}
          </View>

          {proveedor.contacto && (
            <List.Item
              title={proveedor.contacto}
              description="Persona de contacto"
              left={props => <List.Icon {...props} icon="account" />}
            />
          )}
          {proveedor.telefono && (
            <List.Item
              title={proveedor.telefono}
              description="Teléfono"
              left={props => <List.Icon {...props} icon="phone" />}
            />
          )}
          {proveedor.email && (
            <List.Item
              title={proveedor.email}
              description="Email"
              left={props => <List.Icon {...props} icon="email" />}
            />
          )}
          {proveedor.direccion && (
            <>
              <Divider style={styles.divider} />
              <Text style={styles.sectionTitle}>Dirección</Text>
              <Text style={styles.direccion}>{proveedor.direccion}</Text>
            </>
          )}

          {/* Compras recientes */}
          {proveedor.compras_recientes && proveedor.compras_recientes.length > 0 && (
            <>
              <Divider style={styles.divider} />
              <Text style={styles.sectionTitle}>Compras Recientes</Text>
              {proveedor.compras_recientes.map(compra => (
                <List.Item
                  key={compra.id}
                  title={compra.numero_compra}
                  description={`${formatFecha(compra.fecha)} - ${compra.estado}`}
                  right={() => <Text style={styles.totalText}>L. {parseFloat(compra.total).toFixed(2)}</Text>}
                  onPress={() => navigation.navigate('DetalleCompra', { compraId: compra.id })}
                  style={styles.compraItem}
                />
              ))}
            </>
          )}

          <Divider style={styles.divider} />

          <Text style={styles.sectionTitle}>Información</Text>

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Registrado:</Text>
            <Text style={styles.infoValue}>{formatFecha(proveedor.creado_en)}</Text>
          </View>
          {proveedor.actualizado_en && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Última modificación:</Text>
              <Text style={styles.infoValue}>{formatFecha(proveedor.actualizado_en)}</Text>
            </View>
          )}

          {proveedor.notas && (
            <>
              <Divider style={styles.divider} />
              <Text style={styles.sectionTitle}>Notas</Text>
              <Text style={styles.notesText}>{proveedor.notas}</Text>
            </>
          )}
        </Card.Content>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 10 },
  card: { elevation: 2, marginBottom: 20 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  proveedorName: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  rtnText: { fontSize: 13, color: '#888', marginTop: 2 },
  estadoChip: { marginLeft: 8 },
  chipActivo: { backgroundColor: '#d4edda' },
  chipInactivo: { backgroundColor: '#f8d7da' },
  actionButtons: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 12 },
  pedidoButton: { flex: 1, marginRight: 10, backgroundColor: '#007BFF' },
  contactButtons: { flexDirection: 'row', marginBottom: 8 },
  contactButton: { marginRight: 8 },
  divider: { marginVertical: 10 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 8, color: '#0066cc' },
  direccion: { marginVertical: 4, paddingHorizontal: 15, color: '#555' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 4 },
  infoLabel: { fontWeight: 'bold', color: '#555' },
  infoValue: { color: '#333' },
  totalText: { fontWeight: 'bold', color: '#28a745', alignSelf: 'center' },
  compraItem: { borderBottomWidth: 1, borderBottomColor: '#eee' },
  notesText: { fontStyle: 'italic', color: '#666', paddingHorizontal: 15 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 50 },
  loaderText: { marginTop: 10, color: '#666' },
});

export default ProveedorDetalleScreen;
